/**
 * Centralized Access Control System for ParkProUI
 *
 * This file defines all role-based access controls in one place.
 *
 * ─── Two separate access dimensions ──────────────────────────────────────────
 *
 *  1. ROLE  (this file)
 *     Comes from user.role in the JWT / /api/me response.
 *     Controls: who is a sub-agent vs account owner vs ParkPro staff.
 *     Roles:  agentUser → agentAdmin → parkProAdmin
 *
 *  2. PLAN  (src/config/subscriptionConfig.js)
 *     Comes from user.AgencyMaxUsers returned by /api/me.
 *     Controls: does this agency's subscription include a specific feature.
 *     Gate:  AgencyMaxUsers > 1  →  agency plan  →  admin UI visible.
 *
 *  Use BOTH dimensions together for agency admin access checks.
 *  See canSeeAgencyAdmin() in subscriptionConfig.js for the combined gate.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 */

// User roles hierarchy (from lowest to highest access)
export const USER_ROLES = {
  AGENT_USER: 'agentUser',   // Team members added by an agency admin — agent workspace only
  AGENT: 'agent',            // Legacy standalone agent role
  AGENT_ADMIN: 'agentAdmin', // Account owner (ALL signups default to this role)
  PARKPRO_ADMIN: 'parkProAdmin', // Internal ParkPro team — full access
};

// Special users — configured via VITE_SPECIAL_USER env var
export const SPECIAL_USERS = {
  KEVIN: import.meta.env.VITE_SPECIAL_USER,
};

/**
 * Check if user has required role or higher.
 * @param {Object} user - User object
 * @param {string} requiredRole - Required role level
 * @returns {boolean}
 */
export const hasRole = (user, requiredRole) => {
  if (!user || !user.role) return false;

  const roleHierarchy = {
    [USER_ROLES.AGENT_USER]: 0,  // sub-agent: lowest level
    [USER_ROLES.AGENT]: 1,       // legacy standalone agent
    [USER_ROLES.AGENT_ADMIN]: 2, // account owner
    [USER_ROLES.PARKPRO_ADMIN]: 3,
  };

  const userLevel = roleHierarchy[user.role] ?? -1;
  const requiredLevel = roleHierarchy[requiredRole] ?? 999;

  return userLevel >= requiredLevel;
};

/**
 * Check if user is a special user (hardcoded access)
 * @param {Object} user - User object
 * @param {string} specialUser - Special user identifier
 * @returns {boolean}
 */
export const isSpecialUser = (user, specialUser) => {
  if (!user || !user.username) return false;
  return user.username === specialUser;
};

/**
 * Check if user has ParkPro Admin access (role or special user)
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const hasParkProAdminAccess = (user) => {
  return hasRole(user, USER_ROLES.PARKPRO_ADMIN) || 
         isSpecialUser(user, SPECIAL_USERS.KEVIN);
};

/**
 * Check if user has an agency-admin-capable role.
 *
 * ⚠️  Role alone is NOT sufficient to show agency admin UI.
 *     Every account owner is created as agentAdmin regardless of plan.
 *     Use canSeeAgencyAdmin(user) from subscriptionConfig.js for the full
 *     combined gate (role + plan).
 *
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const hasAgencyAdminAccess = (user) => {
  return hasRole(user, USER_ROLES.AGENT_ADMIN);
};

/**
 * Check if user has basic agent access (any authenticated user)
 * @param {Object} user - User object
 * @returns {boolean}
 */
export const hasAgentAccess = (user) => {
  return !!user;
};

// Page Access Control Definitions
export const PAGE_ACCESS = {
  // Core Agent Pages - Available to ALL authenticated users
  DASHBOARD: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Main dashboard for all users'
  },
  
  CLIENT_MANAGEMENT: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Client management for all users'
  },
  
  TRIP_MANAGEMENT: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Trip management for all users'
  },
  
  DISNEY_WORLD: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Disney World trip planning for all users'
  },
  
  COMMISSIONS: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Commission tracking for all users'
  },
  
  LEADS: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Lead management for all users'
  },
  
  COMMUNICATIONS: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Communications hub for all users'
  },
  
  SETTINGS: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'User settings for all users'
  },
  
  // Premium Destinations - ParkPro Admin ONLY
  DISNEYLAND: {
    roles: [USER_ROLES.PARKPRO_ADMIN],
    specialUsers: [SPECIAL_USERS.KEVIN],
    description: 'Disneyland trip planning - ParkPro Admin only'
  },
  
  DISNEY_CRUISE: {
    roles: [USER_ROLES.PARKPRO_ADMIN],
    specialUsers: [SPECIAL_USERS.KEVIN],
    description: 'Disney Cruise trip planning - ParkPro Admin only'
  },
  
  UNIVERSAL_ORLANDO: {
    roles: [USER_ROLES.PARKPRO_ADMIN],
    specialUsers: [SPECIAL_USERS.KEVIN],
    description: 'Universal Orlando trip planning - ParkPro Admin only'
  },
  
  // Agency Admin Pages - Agency Admin and ParkPro Admin
  AGENCY_DASHBOARD: {
    roles: [USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Agency dashboard - Agency Admin and ParkPro Admin only'
  },
  
  AGENT_MANAGEMENT: {
    roles: [USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Agent management - Agency Admin and ParkPro Admin only'
  },
  
  FINANCIAL_MANAGEMENT: {
    roles: [USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Financial management - Agency Admin and ParkPro Admin only'
  },
  
  WHITE_LABELING: {
    roles: [USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'White labeling - Agency Admin and ParkPro Admin only'
  },
  
  AGENCY_SETTINGS: {
    roles: [USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Agency settings - Agency Admin and ParkPro Admin only'
  },
  
  AGENCY_REPORTS: {
    roles: [USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Agency reports - Agency Admin and ParkPro Admin only'
  },
  
  // Sales Pipeline - ParkPro Admin ONLY
  SALES_PIPELINE: {
    roles: [USER_ROLES.PARKPRO_ADMIN],
    specialUsers: [SPECIAL_USERS.KEVIN],
    description: 'Sales pipeline - ParkPro Admin only'
  },
  
  // CRM Trip Management - All Users
  CRM_ADD_TRIP: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Add new trips - All authenticated users'
  },
  
  CRM_CLIENT_DETAILS: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Client details - All authenticated users'
  },
  
  CRM_ADD_CLIENT: {
    roles: [USER_ROLES.AGENT, USER_ROLES.AGENT_ADMIN, USER_ROLES.PARKPRO_ADMIN],
    description: 'Add new clients - All authenticated users'
  }
};

/**
 * Check if user has access to a specific page
 * @param {Object} user - User object
 * @param {string} pageKey - Page key from PAGE_ACCESS
 * @returns {boolean}
 */
export const hasPageAccess = (user, pageKey) => {
  const pageConfig = PAGE_ACCESS[pageKey];
  if (!pageConfig) {
    console.warn(`Page access config not found for: ${pageKey}`);
    return false;
  }
  
  // Check role-based access
  if (pageConfig.roles && pageConfig.roles.length > 0) {
    const hasRequiredRole = pageConfig.roles.some(role => hasRole(user, role));
    if (hasRequiredRole) return true;
  }
  
  // Check special user access
  if (pageConfig.specialUsers && pageConfig.specialUsers.length > 0) {
    const hasSpecialAccess = pageConfig.specialUsers.some(specialUser => 
      isSpecialUser(user, specialUser)
    );
    if (hasSpecialAccess) return true;
  }
  
  return false;
};

/**
 * Get user's accessible pages
 * @param {Object} user - User object
 * @returns {Array} Array of page keys user can access
 */
export const getAccessiblePages = (user) => {
  return Object.keys(PAGE_ACCESS).filter(pageKey => 
    hasPageAccess(user, pageKey)
  );
};

/**
 * Get page access info for debugging
 * @param {Object} user - User object
 * @param {string} pageKey - Page key from PAGE_ACCESS
 * @returns {Object} Access info object
 */
export const getPageAccessInfo = (user, pageKey) => {
  const pageConfig = PAGE_ACCESS[pageKey];
  const hasAccess = hasPageAccess(user, pageKey);
  
  return {
    pageKey,
    hasAccess,
    userRole: user?.role,
    username: user?.username,
    requiredRoles: pageConfig?.roles || [],
    specialUsers: pageConfig?.specialUsers || [],
    description: pageConfig?.description || 'No description available'
  };
};

// Legacy compatibility functions (for gradual migration)
export const hasCRMAccess = (user) => hasAgentAccess(user);
export const hasAgencyAdminAccessLegacy = (user) => hasAgencyAdminAccess(user);
export const hasNewDestinationAccess = (user) => hasParkProAdminAccess(user);

// ─── Role utilities (merged from roleUtils.js) ────────────────────────────────

/**
 * Normalize role value for case-insensitive comparison.
 * @param {string|undefined|null} role
 * @returns {string}
 */
export const normalizeRole = (role) => (role || '').toLowerCase();

/** @param {string|undefined|null} role */
export const isParkProAdmin = (role) => normalizeRole(role) === 'parkproadmin';

/** @param {string|undefined|null} role */
export const isAgentAdmin = (role) => normalizeRole(role) === 'agentadmin';

/** @param {string|undefined|null} role */
export const isAgentUser = (role) => normalizeRole(role) === 'agentuser';

/** @param {string|undefined|null} role */
export const isAgentOrAgentAdmin = (role) => {
  const r = normalizeRole(role);
  return r === 'agent' || r === 'agentadmin';
};

/**
 * Check if the role has ParkPro-level admin access.
 * Used for ParkPro-internal features only (impersonation, platform admin, etc.).
 * Do NOT use this to gate agency admin UI — use canSeeAgencyAdmin() from
 * subscriptionConfig.js which checks both role AND subscription plan.
 *
 * @param {string|undefined|null} role
 * @returns {boolean}
 */
export const canAccessAdmin = (role) => isParkProAdmin(role);

/**
 * Check if the role is capable of accessing agency admin features.
 * Role check only — still requires plan check (AgencyMaxUsers > 1).
 * Use canSeeAgencyAdmin(user) from subscriptionConfig.js for the full gate.
 *
 * @param {string|undefined|null} role
 * @returns {boolean}
 */
export const canAccessAgencyAdmin = (role) => isAgentAdmin(role) || isParkProAdmin(role);

/**
 * Get role-based access helpers from user object.
 * @param {Object|undefined|null} user
 * @returns {{ role: string, isParkProAdmin: boolean, isAgentAdmin: boolean, isAgentUser: boolean, canAccessAdmin: boolean }}
 */
export const getRoleAccess = (user) => {
  const role = normalizeRole(user?.role);
  return {
    role,
    isParkProAdmin: isParkProAdmin(role),
    isAgentAdmin: isAgentAdmin(role),
    isAgentUser: isAgentUser(role),
    canAccessAdmin: canAccessAdmin(role),
  };
};

/**
 * Get the default landing path for a user after login.
 *
 * All users land on the agent dashboard regardless of role.
 * Agency admins with an agency-tier plan will see the "My Agency" section
 * in the nav — they can navigate there themselves.
 * We do NOT auto-redirect to the admin dashboard: that decision belongs to
 * the user, not the app.
 *
 * @param {string|undefined|null} _role  Unused — kept for API compatibility
 * @returns {string}
 */
export const getLandingPathForRole = (_role) => '/agent/dashboard';
